import React, { useState, useEffect } from "react";
import { ArrowLeft, Eye, EyeOff, Copy, RotateCcw, ArrowRight, Monitor, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { db, auth, API_BASE_URL, ACCOUNT_DOMAIN } from "../../firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import {QRCodeSVG} from 'qrcode.react';
import ErrorModal from '../ui/ErrorModal';
import SupportChat from '../Support/SupportChat';
import { useSupportMessages } from '../Support/useSupportMessages';

function DeviceSetup({ familyID, onReturn }) {
    const [familyName, setFamilyName] = useState("");
    const [familyUserID, setFamilyUserID] = useState("");
    const [selectedOption, setSelectedOption] = useState(null);
    const [wifiSSID, setWifiSSID] = useState("");
    const [wifiPassword, setWifiPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [passcode, setPasscode] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [qrGenerated, setQrGenerated] = useState(false);
    const [guestLink, setGuestLink] = useState("");
    const [copyStatus, setCopyStatus] = useState(false);
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [showSupportChat, setShowSupportChat] = useState(false);
    const { unreadCount, fetchUnreadCount } = useSupportMessages();

    useEffect(() => {
        const fetchFamilyData = async () => {
          const familyDocRef = doc(db, "familyInfo", familyID);
          setFamilyUserID(familyID + "@family.higlo.ai");
          try {
            const docSnap = await getDoc(familyDocRef);
            if (docSnap.exists()) {
              setFamilyName(docSnap.data().name);
            }
          } catch (error) {
            console.error("Error fetching family data:", error);
          }
        };
    
        fetchFamilyData();
    }, [familyID]);

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        setQrGenerated(false);
        setWifiSSID("");
        setWifiPassword("");
        setErrorMessage("");
        setShowErrorModal(false);
        setPasscode("");
        setGuestLink("");
        setCurrentStep(1);
        if (option === "invite") {
            handleSubmitInviteSetup();
        }
    };

    const handleGenerateQRCode = async () => {
        // Generate QR code for step 3
        setIsGeneratingQR(true);
        setPasscode("");

        try {
            const currentUser = auth.currentUser;
            const idToken = await currentUser.getIdToken();

            const response = await fetch(`${API_BASE_URL}/user/generatePasscode`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    userEmail: currentUser.email,
                    familyID,
                    type: 'deviceSetup',
                }),
            });

            const data = await response.json();
            if (data.success) {
                setPasscode(data.code);
                setQrGenerated(true);

                // Scroll to the "I'm setting up the Hearth" card after QR code is generated
                setTimeout(() => {
                    const selfSetupCard = document.querySelector('.ring-2.ring-slate-500');
                    if (selfSetupCard) {
                        selfSetupCard.scrollIntoView({ behavior: 'smooth', block: 'end' });
                    }
                }, 100);
            } else {
                console.error(data.error || 'Failed to generate passcode. Please try again.');
                setErrorMessage('Failed to generate passcode. Please try again.');
                setShowErrorModal(true);
                setIsGeneratingQR(false);
                return;
            }
        } catch (error) {
            console.error('Error in generatePasscode Cloud Function:', error);
            setErrorMessage('Failed to generate passcode. Please try again.');
            setShowErrorModal(true);
            setIsGeneratingQR(false);
            return;
        }
        setErrorMessage(""); // Clear message on successful submission
        setShowErrorModal(false);
        setIsGeneratingQR(false);
    };

    const handleSubmitInviteSetup = async () => {
        setIsGeneratingLink(true); // Start loading
        setGuestLink("");
        try {
            const currentUser = auth.currentUser;
            const idToken = await currentUser.getIdToken();

            const response = await fetch(`${API_BASE_URL}/user/generatePasscode`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    userEmail: currentUser.email,
                    familyID,
                    type: 'guestSetup',
                }),
            });

            const data = await response.json();
            if (data.success) {
                const trailerPad = "11";
                const params = {
                    token: data.code,
                    fID: familyID,
                    fName: familyName,
                    ts: data.timestamp._seconds,
                    pad: trailerPad,
                };
                
                // Base-64 encode the parameters
                const encodedParams = btoa(JSON.stringify(params));
                const guestUrl = new URL('/deviceSetupLocal', ACCOUNT_DOMAIN);
                guestUrl.search = new URLSearchParams({
                    data: encodedParams
                }).toString();
                const guestLink = guestUrl.toString();
                setGuestLink(guestLink);
                
                // Scroll to the link card after it's generated
                setTimeout(() => {
                    const linkCardElement = document.getElementById('guest-link-card');
                    if (linkCardElement) {
                        // Get device width to determine if it's mobile
                        const isMobile = window.innerWidth < 768;
                        if (isMobile) {
                            // For mobile, show from the explanation text
                            const explanationElement = document.getElementById('invite-explanation');
                            if (explanationElement) {
                                explanationElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        } else {
                            // For desktop, center the link card
                            linkCardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }
                }, 100);
            } else {
                console.error(data.error || 'Failed to generate Guest link. Please try again.');
                setErrorMessage('Failed to generate Guest link. Please try again.');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error in generatePasscode Cloud Function:', error);
            setErrorMessage('Failed to generate Guest link. Please try again.');
            setShowErrorModal(true);
        } finally {
            setIsGeneratingLink(false); // End loading
        }
    };

    const handleStep1Next = () => {
        setCurrentStep(2);
    };

    const handleStep2Back = () => {
        setCurrentStep(1);
    };

    const handleStep2Next = async () => {
        // Validate WiFi credentials before proceeding
        if (!wifiSSID) {
            setErrorMessage("WiFi SSID cannot be empty.");
            setShowErrorModal(true);
            return;
        }
        if (wifiSSID.includes(';')) {
            setErrorMessage("Your WiFi network name contains a semicolon (;) which is not supported. Please rename your WiFi network or use a different network.");
            setShowErrorModal(true);
            return;
        }
        // if ((!wifiSSID && wifiPassword) || (wifiSSID && !wifiPassword)) {
        //     setErrorMessage("WiFi SSID and Password cannot be empty.");
        //     setShowErrorModal(true);
        //     return;
        // }
        setCurrentStep(3);
        // Automatically generate QR code when moving to step 3
        await handleGenerateQRCode();
    };

    const handleSkipWiFi = async () => {
        setWifiSSID("");
        setWifiPassword("");
        setCurrentStep(3);
        await handleGenerateQRCode();
    };

    const handleStep3Back = () => {
        setCurrentStep(2);
        setQrGenerated(false);
        setPasscode("");
    };

    const handleRegenerateQR = () => {
        setQrGenerated(false);
        setWifiSSID("");
        setWifiPassword("");
        setPasscode("");
        setCurrentStep(1);
    };

    const renderStep1 = () => (
        <div className="max-w-md mx-auto mt-6 space-y-6">
            <div className="text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Monitor className="h-6 w-6 text-gray-700" />
                    </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Connect Hearth to TV</h3>
                <div className="max-w-xs mx-auto">
                    <ol className="text-left space-y-2 text-slate-600">
                        <li>1. Connect the Hearth to your TV.</li>
                        <li>2. Power it up and Open the Lid.</li>
                        <li>3. Hit Next once you see the 'Setup Device' page on the TV.</li>
                    </ol>
                </div>
            </div>
            
            <div className="flex justify-center pt-4">
                <Button
                    variant="default"
                    onClick={handleStep1Next}
                    className="w-32 bg-gray-800 hover:bg-gray-700 flex items-center gap-2"
                >
                    Next
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="max-w-md mx-auto mt-6 space-y-6">
            <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">WiFi Setup</h3>
                <p className="text-slate-600">Enter your WiFi SSID and Password to connect</p>
            </div>
            
            <div className="space-y-4">
                <Input 
                    type="text" 
                    placeholder="WiFi SSID" 
                    value={wifiSSID} 
                    onChange={(e) => setWifiSSID(e.target.value)} 
                />
                
                <div className="relative">
                    <Input 
                        type={showPassword ? "text" : "password"} 
                        placeholder="WiFi Password" 
                        value={wifiPassword} 
                        onChange={(e) => setWifiPassword(e.target.value)} 
                        className="pr-10"
                    />
                    <Button 
                        variant="ghost"
                        size="icon"
                        type="button" 
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
                    >
                        {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                </div>
            </div>
            
            <div className="flex gap-3 pt-4">
                <Button
                    variant="outline"
                    onClick={handleStep2Back}
                    className="w-full"
                >
                    Back
                </Button>
                <Button
                    variant="default"
                    onClick={handleStep2Next}
                    className="w-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center gap-2"
                >
                    Next
                    <ArrowRight className="h-4 w-4" />
                </Button>
            </div>

            <div className="text-center pt-2">
                <button
                    onClick={handleSkipWiFi}
                    className="text-sm text-slate-500 hover:text-slate-700 underline"
                >
                    Hearth is already connected to WiFi
                </button>
            </div>
        </div>
    );

    const renderStep3 = () => {
        if (!qrGenerated) {
            return (
                <div className="max-w-md mx-auto mt-6 space-y-6">
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4">Generating QR Code</h3>
                        <p className="text-slate-600">Please wait while we generate your QR code...</p>
                        <div className="flex items-center justify-center mt-4 space-x-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
                            <span className="text-slate-600">Generating...</span>
                        </div>
                    </div>
                    
                    <div className="flex justify-center pt-4">
                        <Button
                            variant="outline"
                            onClick={handleStep3Back}
                            className="w-32"
                        >
                            Back
                        </Button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="max-w-2xl mx-auto mt-6 space-y-6">
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4">QR Code</h3>
                        <p className="text-slate-600">
                            {wifiSSID
                                ? "Show the QR code below to the Hearth to connect. This QR code is valid for 2 hours."
                                : "Show the QR code below to the Hearth to sign in. This QR code is valid for 2 hours. WiFi setup will be skipped."
                            }
                        </p>
                    </div>
                    
                    <div className="flex justify-center">
                        <div className="max-w-full overflow-hidden">
                            <QRCodeSVG
                                value={[`${wifiSSID};`, `${wifiPassword};`, `${familyUserID};`, `${passcode};`]}
                                size={280}
                                className="w-full h-auto max-w-70"
                                id="qr-code-element"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                            variant="outline"
                            onClick={handleStep3Back}
                            className="flex items-center justify-center gap-2"
                        >
                            Back
                        </Button>
                    </div>
                </div>
            );
        }
    };

    const renderSelfSetupContent = () => {
        switch (currentStep) {
            case 1:
                return renderStep1();
            case 2:
                return renderStep2();
            case 3:
                return renderStep3();
            default:
                return renderStep1();
        }
    };

    const renderInviteSetupContent = () => {
        if (isGeneratingLink) {
            return (
                <div className="flex items-center justify-center mt-6 space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                    <p className="text-slate-600">Generating link...</p>
                </div>
            );
        } else if (guestLink) {
            return (
                <div className="mt-6 space-y-4">
                    <div className="max-w-2xl mx-auto">
                        <p id="invite-explanation" className="text-justify text-slate-600 mb-4">
                            A one time link is generated below. Share it with the person setting up the Hearth. The link expires in 1 week.
                        </p>
                        
                        <Card id="guest-link-card" className="mb-4 bg-slate-50">
                            <CardContent className="p-4">
                                <div className="break-all">
                                    <a 
                                        href={guestLink} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                        {guestLink}
                                    </a>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <div className="flex flex-col items-center gap-3 max-w-[200px] mx-auto w-full">
                            <Button 
                                variant="default"
                                onClick={() => {
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                        navigator.clipboard.writeText(guestLink)
                                            .then(() => {
                                                setCopyStatus(true);
                                                setTimeout(() => setCopyStatus(false), 2000);
                                            })
                                            .catch(err => {
                                                console.error('Failed to copy: ', err);
                                                fallbackCopyTextToClipboard(guestLink);
                                            });
                                    } else {
                                        fallbackCopyTextToClipboard(guestLink);
                                    }
                                }}
                                className={`w-full flex items-center justify-center gap-2 ${copyStatus ? 'bg-gray-500 hover:bg-gray-600' : 'bg-gray-800 hover:bg-gray-700'}`}
                            >
                                <Copy className="h-4 w-4" />
                                {copyStatus ? "Copied!" : "Copy Link"}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleSubmitInviteSetup}
                                className="w-full flex items-center justify-center gap-2"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Re-generate Link
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    const fallbackCopyTextToClipboard = (text) => {
        try {
            // Create a temporary textarea element
            const textArea = document.createElement("textarea");
            
            // Set its value to the text to copy
            textArea.value = text;
            
            // Make it invisible
            textArea.style.position = "fixed";
            textArea.style.top = 0;
            textArea.style.left = 0;
            textArea.style.width = "2em";
            textArea.style.height = "2em";
            textArea.style.padding = 0;
            textArea.style.border = "none";
            textArea.style.outline = "none";
            textArea.style.boxShadow = "none";
            textArea.style.background = "transparent";
            
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            // Try to copy using the older document.execCommand method
            const successful = document.execCommand('copy');
            
            // Remove the temporary element
            document.body.removeChild(textArea);
            
            // Update status
            if (successful) {
                setCopyStatus(true);
                setTimeout(() => setCopyStatus(false), 2000);
            } else {
                console.error('Fallback copy failed');
                setErrorMessage('Copy feature not supported on this device. Please manually copy the link.');
                setShowErrorModal(true);
            }
        } catch (err) {
            console.error('Fallback copy error:', err);
            setErrorMessage('Copy feature not supported on this device. Please manually copy the link.');
            setShowErrorModal(true);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-start gap-4">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                        <Button
                            onClick={onReturn}
                            variant="ghost"
                            size="icon"
                            className="flex-shrink-0"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl font-bold text-slate-900">Setup Hearth</h1>
                            <p className="text-slate-600">Sign in the Hearth device and connect it to WiFi</p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSupportChat(true)}
                        className="flex items-center gap-1.5 relative flex-shrink-0"
                    >
                        <MessageCircle className="h-4 w-4" />
                        <span className="text-xs sm:text-sm">Feedback/Help</span>
                        {unreadCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </Button>
                </div>

                {/* Setup Options */}
                <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Self Setup Card */}
                        <Card className={`${selectedOption === "self" ? "ring-2 ring-slate-500" : ""}`}>
                            <CardHeader className="text-center">
                                <CardTitle className="text-lg">I'm setting up the Hearth</CardTitle>
                                {/* <CardDescription>
                                    Set up the device yourself using your WiFi credentials
                                </CardDescription> */}
                            </CardHeader>
                            <CardContent className="text-center">
                                <Button 
                                    variant={selectedOption === "self" ? "default" : "outline"}
                                    onClick={() => handleOptionSelect("self")}
                                    className={`w-32 ${selectedOption === "self" ? "bg-gray-800 text-white hover:bg-gray-700" : ""}`}
                                >
                                    Select
                                </Button>
                                
                                {/* Progress indicator for self setup */}
                                {selectedOption === "self" && (
                                    <div className="flex justify-center mt-4 mb-2">
                                        <div className="flex items-center space-x-2">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                currentStep >= 1 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
                                            }`}>
                                                1
                                            </div>
                                            <div className={`w-8 h-1 ${currentStep >= 2 ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                currentStep >= 2 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
                                            }`}>
                                                2
                                            </div>
                                            <div className={`w-8 h-1 ${currentStep >= 3 ? 'bg-gray-600' : 'bg-gray-200'}`}></div>
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                                currentStep >= 3 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-600'
                                            }`}>
                                                3
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedOption === "self" && renderSelfSetupContent()}
                            </CardContent>
                        </Card>

                        {/* Invite Setup Card */}
                        <Card className={`${selectedOption === "invite" ? "ring-2 ring-slate-500" : ""}`}>
                            <CardHeader className="text-center">
                                <CardTitle className="text-lg">Someone else will set up the Hearth</CardTitle>
                                {/* <CardDescription>
                                    Enable someone else to set up the Hearth
                                </CardDescription> */}
                            </CardHeader>
                            <CardContent className="text-center">
                                <Button 
                                    variant={selectedOption === "invite" ? "default" : "outline"}
                                    onClick={() => handleOptionSelect("invite")}
                                    className={`w-32 ${selectedOption === "invite" ? "bg-gray-800 text-white hover:bg-gray-700" : ""}`}
                                >
                                    Select
                                </Button>
                                
                                {selectedOption === "invite" && renderInviteSetupContent()}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Error Modal */}
                    <ErrorModal
                        isOpen={showErrorModal}
                        message={errorMessage}
                        onClose={() => {
                            setShowErrorModal(false);
                            setErrorMessage("");
                        }}
                    />
                </div>
            </div>

            {/* Support Chat */}
            {showSupportChat && (
                <SupportChat
                    onClose={() => {
                        setShowSupportChat(false);
                        fetchUnreadCount();
                    }}
                />
            )}
        </div>
    );
}

export default DeviceSetup;
